// src/components/LifeMission.jsx
import React, { useState } from 'react';

const LifeMission = ({ user }) => {
  const [activeSection, setActiveSection] = useState('executive');
  
  // --- FINANCIAL CALCULATOR STATE ---
  const [calcInputs, setCalcInputs] = useState({
    initialInvestment: 10000,
    cashFlows: "3000, 4000, 5000, 6000", // Comma separated annual flows
    discountRate: 10, // i (percent)
    netProfit: 8000, // For ROI calculation
    costOfInvestment: 10000, // For ROI calculation
    periodicRate: 1.5, // For APR
    periodsPerYear: 12, // For APR
  });

  const [results, setResults] = useState(null);

  // --- CALCULATION LOGIC ---
  const calculateFinancials = () => {
    const initial = parseFloat(calcInputs.initialInvestment);
    const flows = calcInputs.cashFlows.split(',').map(num => parseFloat(num.trim()));
    const rate = parseFloat(calcInputs.discountRate) / 100;
    
    // 1. NPV Calculation [1.4.3]
    // Formula: Sum(Ct / (1+i)^t) - Initial Investment
    let npv = -initial;
    flows.forEach((Ct, t_minus_1) => {
      const t = t_minus_1 + 1;
      npv += Ct / Math.pow((1 + rate), t);
    });

    // 2. IRR Calculation [1.5.3] (Iterative Newton-Raphson approximation)
    // Formula: 0 = Sum(Ct / (1+IRR)^t) - Initial
    let irr = 0.1; // Initial guess (10%)
    for (let i = 0; i < 1000; i++) {
      let npv_irr = -initial;
      let derivative = 0;
      flows.forEach((Ct, t_minus_1) => {
        const t = t_minus_1 + 1;
        npv_irr += Ct / Math.pow((1 + irr), t);
        derivative -= (t * Ct) / Math.pow((1 + irr), t + 1);
      });
      const newIrr = irr - npv_irr / derivative;
      if (Math.abs(newIrr - irr) < 0.0001) {
        irr = newIrr;
        break;
      }
      irr = newIrr;
    }

    // 3. ROI Calculation [1.6.2]
    // Formula: ((Net Profit - Cost) / Cost) * 100
    // Note: The prompt formula was (Net Profit - Cost) / Cost. 
    // Sometimes Net Profit is already (Revenue - Cost). 
    // We will use your input fields specifically.
    const roiNetProfit = parseFloat(calcInputs.netProfit);
    const roiCost = parseFloat(calcInputs.costOfInvestment);
    const roi = ((roiNetProfit - roiCost) / roiCost) * 100;

    // 4. Payback Period [1.7.1]
    // Formula: Initial Investment / Average Annual Cash Flow
    const totalCashFlow = flows.reduce((a, b) => a + b, 0);
    const avgCashFlow = totalCashFlow / flows.length;
    const payback = initial / avgCashFlow;

    // 5. APR Calculation [1.3.1]
    // Formula: Periodic Rate * Periods per Year
    const periodic = parseFloat(calcInputs.periodicRate);
    const periods = parseFloat(calcInputs.periodsPerYear);
    const apr = periodic * periods;

    setResults({
      npv: npv.toFixed(2),
      irr: (irr * 100).toFixed(2),
      roi: roi.toFixed(2),
      payback: payback.toFixed(2),
      apr: apr.toFixed(2)
    });
  };

  const sections = [
    { id: 'executive', label: 'Executive Summary' },
    { id: 'company', label: 'Company Description' },
    { id: 'market', label: 'Market Analysis' },
    { id: 'products', label: 'Products & Services' },
    { id: 'marketing', label: 'Marketing & Sales' },
    { id: 'team', label: 'Management Team' },
    { id: 'operations', label: 'Operating Plan' },
    { id: 'funding', label: 'Funding Request' },
    { id: 'financials', label: 'Financial Projections' },
    { id: 'appendix', label: 'Appendix' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 min-h-[700px] flex flex-col md:flex-row font-sans">
      
      {/* --- SIDEBAR NAV --- */}
      <div className="w-full md:w-64 bg-gray-50 border-r border-gray-200 p-4">
        <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
          <span className="text-2xl mr-2">🌟</span> Life Mission
        </h3>
        <nav className="space-y-1">
          {sections.map((sec) => (
            <button
              key={sec.id}
              onClick={() => setActiveSection(sec.id)}
              className={`w-full text-left px-4 py-3 rounded text-sm font-medium transition ${
                activeSection === sec.id 
                  ? 'bg-indigo-100 text-indigo-700 border-l-4 border-indigo-600' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {sec.label}
            </button>
          ))}
        </nav>
        
        {/* Investor Insights Box */}
        <div className="mt-8 p-4 bg-yellow-50 rounded border border-yellow-200 text-xs">
          <h4 className="font-bold text-yellow-800 mb-2">💡 Key Investor Insights</h4>
          <ul className="space-y-2 text-yellow-900 list-disc pl-3">
            <li><strong>Focus on Opportunity:</strong> Emphasize the market gap.</li>
            <li><strong>Show Traction:</strong> Evidence of early sales or partnerships.</li>
            <li><strong>Be Transparent:</strong> Acknowledge risks and exit strategies.</li>
          </ul>
        </div>
      </div>

      {/* --- MAIN CONTENT EDITOR --- */}
      <div className="flex-1 p-8 overflow-y-auto">
        
        {/* Header for Section */}
        <div className="mb-6 pb-4 border-b">
          <h2 className="text-2xl font-bold text-gray-800">
            {sections.find(s => s.id === activeSection)?.label}
          </h2>
          <p className="text-sm text-gray-500 italic mt-1">Standard Business Planning Model</p>
        </div>

        {/* Dynamic Content Forms */}
        <div className="space-y-6">
          
          {activeSection === 'financials' ? (
            <div className="animate-fade-in">
              {/* --- FINANCIAL CALCULATOR --- */}
              <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-100 mb-8">
                <h4 className="font-bold text-indigo-900 mb-4 flex items-center gap-2">
                  🧮 Investor Metric Calculator
                </h4>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Row 1: NPV & IRR Inputs */}
                  <div>
                    <label className="block text-xs font-bold text-indigo-800 mb-1">Initial Investment ($)</label>
                    <input 
                      type="number" 
                      value={calcInputs.initialInvestment}
                      onChange={(e) => setCalcInputs({...calcInputs, initialInvestment: e.target.value})}
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-indigo-800 mb-1">Cash Flows (Years 1, 2, 3... comma separated)</label>
                    <input 
                      type="text" 
                      value={calcInputs.cashFlows}
                      onChange={(e) => setCalcInputs({...calcInputs, cashFlows: e.target.value})}
                      className="w-full border p-2 rounded"
                      placeholder="3000, 4000, 5000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-indigo-800 mb-1">Discount Rate (i) %</label>
                    <input 
                      type="number" 
                      value={calcInputs.discountRate}
                      onChange={(e) => setCalcInputs({...calcInputs, discountRate: e.target.value})}
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  
                  {/* Row 2: ROI Inputs */}
                  <div className="border-t pt-2 col-span-2 grid grid-cols-2 gap-6">
                     <div>
                        <label className="block text-xs font-bold text-indigo-800 mb-1">Net Profit ($) (For ROI)</label>
                        <input 
                          type="number" 
                          value={calcInputs.netProfit}
                          onChange={(e) => setCalcInputs({...calcInputs, netProfit: e.target.value})}
                          className="w-full border p-2 rounded"
                        />
                     </div>
                     <div>
                        <label className="block text-xs font-bold text-indigo-800 mb-1">Cost of Investment ($) (For ROI)</label>
                        <input 
                          type="number" 
                          value={calcInputs.costOfInvestment}
                          onChange={(e) => setCalcInputs({...calcInputs, costOfInvestment: e.target.value})}
                          className="w-full border p-2 rounded"
                        />
                     </div>
                  </div>

                  {/* Row 3: APR Inputs */}
                  <div className="border-t pt-2 col-span-2 grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-indigo-800 mb-1">Periodic Interest Rate (%)</label>
                      <input 
                        type="number" 
                        value={calcInputs.periodicRate}
                        onChange={(e) => setCalcInputs({...calcInputs, periodicRate: e.target.value})}
                        className="w-full border p-2 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-indigo-800 mb-1">Periods per Year</label>
                      <input 
                        type="number" 
                        value={calcInputs.periodsPerYear}
                        onChange={(e) => setCalcInputs({...calcInputs, periodsPerYear: e.target.value})}
                        className="w-full border p-2 rounded"
                      />
                    </div>
                  </div>
                </div>

                <button 
                  onClick={calculateFinancials}
                  className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700 transition mb-6"
                >
                  Calculate Indicators
                </button>

                {/* RESULTS DISPLAY */}
                {results && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
                    <div className="bg-white p-3 rounded shadow-sm border">
                      <div className="text-xs text-gray-500 font-bold">NPV</div>
                      <div className={`font-bold ${parseFloat(results.npv) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${results.npv}</div>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm border">
                      <div className="text-xs text-gray-500 font-bold">IRR</div>
                      <div className="font-bold text-blue-600">{results.irr}%</div>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm border">
                      <div className="text-xs text-gray-500 font-bold">ROI</div>
                      <div className="font-bold text-green-600">{results.roi}%</div>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm border">
                      <div className="text-xs text-gray-500 font-bold">Payback</div>
                      <div className="font-bold text-purple-600">{results.payback} Yrs</div>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm border">
                      <div className="text-xs text-gray-500 font-bold">APR</div>
                      <div className="font-bold text-orange-600">{results.apr}%</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Standard Narrative Area */}
              <p className="text-sm text-gray-600 mb-4">Narrative: Forecasted income statements, balance sheets, and cash flow assumptions.</p>
              <textarea className="w-full h-32 border p-3 rounded mt-2" placeholder="Write your financial assumptions and context here..." />
            </div>
          ) : (
            // --- STANDARD VIEW FOR OTHER TABS ---
            <div className="animate-fade-in space-y-6">
              {/* Executive Summary Tip */}
              {activeSection === 'executive' && (
                <div className="bg-blue-50 p-4 rounded mb-4 text-sm text-blue-800">
                  <strong>Tip:</strong> Write this section <em>last</em>. It provides a compelling overview of your entire plan.
                </div>
              )}
              
              {/* Content Areas */}
              <textarea 
                className="w-full h-96 border p-4 rounded focus:ring-2 focus:ring-indigo-500 text-gray-700" 
                placeholder={`Enter details for ${sections.find(s => s.id === activeSection)?.label}...`} 
              />
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t flex justify-end gap-4">
          <button className="text-gray-500 hover:text-gray-800">Save Draft</button>
          <button className="bg-indigo-600 text-white px-6 py-2 rounded shadow hover:bg-indigo-700">Export Business Plan (PDF)</button>
        </div>

      </div>
    </div>
  );
};

export default LifeMission;